import roles from '@/constants/roles';
import { useAppStore } from '@/store/useAppStore';
import { TUser } from '@/types/User.types';

export type TAuthFlags = {
  user: TUser | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isMemberCare: boolean;
  isOnboarded: boolean;
};

/**
 * Role predicates derived from a user. Single source of truth, used by both the
 * React context and the router snapshot below so the two cannot drift.
 *
 * isOnboarded is derived rather than stored, and three guards plus
 * SidebarContentWrapper depend on it — a snapshot without it would break
 * member-care onboarding outright.
 */
export const deriveAuthFlags = (user: TUser | null): TAuthFlags => {
  const isSuperAdmin = !!user?.roles.includes(roles.superAdmin);
  const isMemberCare = !!user?.roles.includes(roles.memberCare);

  return {
    user,
    isAuthenticated: !!user,
    isSuperAdmin,
    isMemberCare,
    // superadmin is onboarded by default, and membercare only if their sex is existent
    isOnboarded: isSuperAdmin || (isMemberCare && !!user?.user_metadata.sex),
  };
};

/**
 * The current auth state, read synchronously from the store.
 *
 * Route guards must use this rather than `context.auth`. RouterProvider merges a
 * new context during render, but that does not re-run `beforeLoad` for matches
 * already on screen — so a guard reading the context can see the pre-sign-in (or
 * pre-sign-out) value, depending on whether React has re-rendered yet.
 */
export const getAuthSnapshot = (): TAuthFlags => deriveAuthFlags(useAppStore.getState().user);
